// frontend/src/pages/app/ingress/IngressChatPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ApiError } from "../../lib/api";
import {
  fetchIngressAppBySlug,
  createThread,
  getThread,
  submitTurn,
  pollUntilTerminal,
  getRequestOutput,
  tryListThreads,
  tryGetAdminRequestDetail,
  IngressAppPublic,
  IngressThread,
  IngressMessage,
  appKeyStorageKey,
  appThreadsStorageKey,
  appSelectedThreadStorageKey,
  appDraftActionStorageKey,
  getSessionJson,
  setSessionJson,
  getSessionStr,
  setSessionStr,
} from "../../lib/ingressChatClient";

import FeedbackWidget from "./FeedbackWidget";

type LocalThreadMeta = {
  id: string;
  title: string;
  created_at?: string;
  updated_at?: string;
  message_count?: number;
};

type TurnMeta = {
  request_id: string;
  status: string;
  error?: string | null;
  created_at?: string;
  finished_at?: string | null;
  output?: any | null;
  adminDetail?: any | null;
};

function fmtDt(s?: string | null) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return String(s);
  }
}

function safeString(v: any) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function isObject(v: any): v is Record<string, any> {
  return v && typeof v === "object" && !Array.isArray(v);
}

function getRequiredKeys(schema: any): string[] {
  const req = isObject(schema) && Array.isArray(schema.required) ? schema.required : [];
  return req.filter((x: any) => typeof x === "string" && x.trim().length > 0);
}

function getProperties(schema: any): Record<string, any> {
  if (!isObject(schema) || !isObject(schema.properties)) return {};
  return schema.properties as Record<string, any>;
}

function getUiFields(uiSchema: any): Array<{ key: string; label?: string; widget?: string; placeholder?: string }> {
  if (!isObject(uiSchema) || !Array.isArray(uiSchema.fields)) return [];
  return uiSchema.fields
    .map((f: any) => ({
      key: safeString(f.key),
      label: safeString(f.label || f.key),
      widget: safeString(f.widget || "text"),
      placeholder: safeString(f.placeholder || ""),
    }))
    .filter((f) => !!f.key);
}

function pickPrimaryTextField(schema: any, uiSchema: any): string {
  const required = getRequiredKeys(schema);
  const props = getProperties(schema);
  const uiFields = getUiFields(uiSchema);

  if (required.includes("transcript")) return "transcript";
  if (required.includes("text")) return "text";

  // UI schema: first textarea field
  const ta = uiFields.find((f) => (f.widget || "").toLowerCase() === "textarea");
  if (ta?.key) return ta.key;

  // fallback: first required string field
  for (const k of required) {
    const p = props[k];
    if (isObject(p) && (p.type === "string" || Array.isArray(p.enum))) return k;
  }

  // last resort
  if (required[0]) return required[0];
  if (props["text"]) return "text";
  return "text";
}

function hasActionEnum(schema: any): string[] {
  const props = getProperties(schema);
  const action = props["action"];
  if (isObject(action) && Array.isArray(action.enum)) {
    return action.enum.filter((x: any) => typeof x === "string");
  }
  return [];
}

function enumOptionsForKey(schema: any, key: string): string[] {
  const props = getProperties(schema);
  const p = props[key];
  if (isObject(p) && Array.isArray((p as any).enum)) {
    return (p as any).enum.filter((x: any) => typeof x === "string");
  }
  return [];
}

function coerceInputsBySchema(raw: Record<string, any>, schema: any): Record<string, any> {
  const props = getProperties(schema);
  const out: Record<string, any> = { ...(raw || {}) };

  for (const k of Object.keys(out)) {
    const ps = props[k];
    if (!isObject(ps)) continue;

    const t = String((ps as any).type || "").toLowerCase();
    const v = out[k];

    if (v == null) continue;

    if (t === "integer") {
      if (typeof v === "number") continue;
      if (typeof v === "string") {
        const s = v.trim();
        if (!s) continue;
        const n = Number(s);
        if (Number.isFinite(n)) out[k] = Math.trunc(n);
      }
    } else if (t === "number") {
      if (typeof v === "number") continue;
      if (typeof v === "string") {
        const s = v.trim();
        if (!s) continue;
        const n = Number(s);
        if (Number.isFinite(n)) out[k] = n;
      }
    } else if (t === "boolean") {
      if (typeof v === "boolean") continue;
      if (typeof v === "string") {
        const s = v.trim().toLowerCase();
        if (s === "true") out[k] = true;
        if (s === "false") out[k] = false;
      }
    }
  }

  return out;
}

function isEmptyValue(v: any): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim().length === 0;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

function pretty(v: any) {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function isArrayOfObjects(v: any): v is Array<Record<string, any>> {
  return Array.isArray(v) && v.every((x) => x && typeof x === "object" && !Array.isArray(x));
}

function StructuredValueView({ value }: { value: any }) {
  // Array of objects -> table
  if (isArrayOfObjects(value)) {
    const cols = Array.from(
      value.reduce((set, row) => {
        Object.keys(row || {}).forEach((k) => set.add(k));
        return set;
      }, new Set<string>())
    );

    return (
      <div className="mt-2 border rounded overflow-auto bg-white">
        <table className="min-w-full text-sm">
          <thead className="text-xs text-gray-500 bg-gray-50">
            <tr className="border-b">
              {cols.map((c) => (
                <th key={c} className="text-left py-2 px-3 whitespace-nowrap">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {value.map((row: any, i: number) => (
              <tr key={i} className="border-b last:border-b-0">
                {cols.map((c) => {
                  const v = row?.[c];
                  const s =
                    v == null
                      ? "—"
                      : typeof v === "string"
                      ? v
                      : typeof v === "number" || typeof v === "boolean"
                      ? String(v)
                      : pretty(v);
                  return (
                    <td key={c} className="py-2 px-3 align-top">
                      <div className="whitespace-pre-wrap">{s}</div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Object -> key/value table
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value);
    return (
      <div className="mt-2 border rounded overflow-auto bg-white">
        <table className="min-w-full text-sm">
          <thead className="text-xs text-gray-500 bg-gray-50">
            <tr className="border-b">
              <th className="text-left py-2 px-3">Key</th>
              <th className="text-left py-2 px-3">Value</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([k, v]) => (
              <tr key={k} className="border-b last:border-b-0">
                <td className="py-2 px-3 font-mono text-xs whitespace-nowrap">{k}</td>
                <td className="py-2 px-3">
                  <div className="whitespace-pre-wrap">{v == null ? "—" : typeof v === "string" ? v : pretty(v)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Fallback
  return <div className="mt-2 whitespace-pre-wrap">{String(value ?? "")}</div>;
}

function statusBadge(status: string) {
  const s = (status || "").toLowerCase();
  const base = "inline-flex items-center px-2 py-1 rounded border text-xs";
  if (s === "succeeded") return `${base} border-green-200 bg-green-50 text-green-800`;
  if (s === "failed") return `${base} border-red-200 bg-red-50 text-red-800`;
  if (s === "running") return `${base} border-blue-200 bg-blue-50 text-blue-800`;
  if (s === "queued") return `${base} border-gray-200 bg-gray-50 text-gray-700`;
  return `${base} border-gray-200 bg-white text-gray-700`;
}

function buildAuditRows(adminDetail: any, output: any) {
  // Prefer adminDetail.normalized_inputs._ctx if available
  const ctx =
    adminDetail?.normalized_inputs?._ctx ??
    adminDetail?.normalized_inputs?._CTX ??
    adminDetail?._ctx ??
    null;

  const snap = ctx?.memory_snapshot ?? null;
  const applied = ctx?.memory_applied ?? null;

  const effectiveInputs =
    adminDetail?.normalized_inputs?.inputs ??
    adminDetail?.normalized_inputs?.Inputs ??
    null;

  const keys = new Set<string>();
  if (isObject(snap)) Object.keys(snap).forEach((k) => keys.add(k));
  if (isObject(applied)) Object.keys(applied).forEach((k) => keys.add(k));
  if (isObject(effectiveInputs)) Object.keys(effectiveInputs).forEach((k) => keys.add(k));

  const rows = Array.from(keys).sort().map((k) => {
    const snapshotVal = isObject(snap) ? snap[k] : undefined;
    const appliedVal = isObject(applied) ? applied[k] : undefined;
    const effectiveVal = isObject(effectiveInputs) ? effectiveInputs[k] : undefined;
    return { key: k, snapshotVal, applied: appliedVal !== undefined, appliedVal, effectiveVal };
  });

  return { ctx, rows };
}

function isUuidLike(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s || ""
  );
}

function getTenantUuidFromLocation(search: string): string | null {
  try {
    const sp = new URLSearchParams(search || "");
    const tid = String(sp.get("tenant_id") || "").trim();
    if (tid && isUuidLike(tid)) return tid;
    return null;
  } catch {
    return null;
  }
}

export default function IngressChatPage() {
  const { slug } = useParams();
  const loc = useLocation();

  // ✅ tenant_id must come from the URL for end-user pages
  // (prevents root fallback + prevents cross-tenant sessionStorage collisions)
  const tenantUuid = useMemo(() => {
    const fromUrl = getTenantUuidFromLocation(loc.search);
    return fromUrl || "";
  }, [loc.search]);

  const [app, setApp] = useState<IngressAppPublic | null>(null);
  const [loadingApp, setLoadingApp] = useState(true);
  const [appErr, setAppErr] = useState<string | null>(null);

  const [apiKey, setApiKey] = useState<string>("");
  const [apiKeyErr, setApiKeyErr] = useState<string | null>(null);

  const [threads, setThreads] = useState<LocalThreadMeta[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string>("");

  const [threadObj, setThreadObj] = useState<IngressThread | null>(null);
  const [messages, setMessages] = useState<IngressMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [threadErr, setThreadErr] = useState<string | null>(null);

  const [action, setAction] = useState<string>("");
  const [messageText, setMessageText] = useState<string>("");
  const [useMemoryDefaults, setUseMemoryDefaults] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedInputs, setAdvancedInputs] = useState<Record<string, any>>({});

  const [turns, setTurns] = useState<Record<string, TurnMeta>>({}); // request_id -> details
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [creatingThread, setCreatingThread] = useState(false);

  const bottomRef = useRef<HTMLDivElement | null>(null);

  const authMode = useMemo(() => (app?.auth_mode || "tenant").toLowerCase(), [app?.auth_mode]);

  const schema = useMemo(() => app?.input_json_schema || { type: "object" }, [app?.input_json_schema]);
  const uiSchema = useMemo(() => app?.ui_schema || {}, [app?.ui_schema]);

  const requiredKeys = useMemo(() => getRequiredKeys(schema), [schema]);
  const props = useMemo(() => getProperties(schema), [schema]);
  const uiFields = useMemo(() => getUiFields(uiSchema), [uiSchema]);

  const actionEnum = useMemo(() => hasActionEnum(schema), [schema]);
  const primaryTextField = useMemo(() => pickPrimaryTextField(schema, uiSchema), [schema, uiSchema]);
  // Required enum fields (e.g., tier) must be provided by UI (memory does NOT backfill required keys)
  const requiredEnumFields = useMemo(() => {
    const req = getRequiredKeys(schema);
    return req
      .filter((k) => k !== "action" && k !== primaryTextField)
      .map((k) => ({ key: k, options: enumOptionsForKey(schema, k) }))
      .filter((x) => x.options.length > 0);
  }, [schema, primaryTextField]);

  const requiredNumberFields = useMemo(() => {
  const req = getRequiredKeys(schema);
  return req
    .filter((k) => k !== "action" && k !== primaryTextField)
    .map((k) => ({ key: k, schema: props[k] }))
    .filter(
      (x) =>
        isObject(x.schema) &&
        (String((x.schema as any).type || "").toLowerCase() === "integer" ||
          String((x.schema as any).type || "").toLowerCase() === "number")
    )
    .map((x) => ({
      key: x.key,
      type: String((x.schema as any).type || "number").toLowerCase(),
      minimum: typeof (x.schema as any).minimum === "number" ? (x.schema as any).minimum : undefined,
      maximum: typeof (x.schema as any).maximum === "number" ? (x.schema as any).maximum : undefined,
    }));
  }, [schema, props, primaryTextField]);

  const runnerUrl = useMemo(() => (slug ? `/app/ingress/${slug}` : "/app/ingress"), [slug]);

  const storageKey = useMemo(
    () => (slug && tenantUuid ? appKeyStorageKey(slug, tenantUuid) : ""),
    [slug, tenantUuid]
  );
  const threadsStorageKey = useMemo(
    () => (slug && tenantUuid ? appThreadsStorageKey(slug, tenantUuid) : ""),
    [slug, tenantUuid]
  );
  const selectedThreadStorageKey = useMemo(
    () => (slug && tenantUuid ? appSelectedThreadStorageKey(slug, tenantUuid) : ""),
    [slug, tenantUuid]
  );
  const draftActionStorageKey = useMemo(
    () => (slug && tenantUuid ? appDraftActionStorageKey(slug, tenantUuid) : ""),
    [slug, tenantUuid]
  );

  // Default required enum fields to first option (unless user already set something)
  useEffect(() => {
    if (!requiredEnumFields.length) return;

    setAdvancedInputs((prev) => {
      let changed = false;
      const next = { ...prev };

      for (const f of requiredEnumFields) {
        if (next[f.key] == null || String(next[f.key]).trim() === "") {
          next[f.key] = f.options[0];
          changed = true;
        }
      }

      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredEnumFields.map((x) => `${x.key}:${x.options.join(",")}`).join("|")]);

  // load app
  useEffect(() => {
    let mounted = true;
    async function run() {
      if (!slug) return;
      setLoadingApp(true);
      setAppErr(null);
      try {
        const a = await fetchIngressAppBySlug(slug);
        if (!mounted) return;
        setApp(a);

        // load stored api key
        if (storageKey) setApiKey(getSessionStr(storageKey));

        // load stored threads list
        if (threadsStorageKey) {
          const local = getSessionJson<LocalThreadMeta[]>(threadsStorageKey, []);
          setThreads(local);
        }

        // load selected thread
        if (selectedThreadStorageKey) {
          const st = getSessionStr(selectedThreadStorageKey);
          if (st) setSelectedThreadId(st);
        }

        // load draft action
        if (draftActionStorageKey) {
          const da = getSessionStr(draftActionStorageKey);
          if (da) setAction(da);
        }

        // default action from schema enum
        if (!getSessionStr(draftActionStorageKey) && actionEnum.length > 0) {
          setAction(actionEnum[0]);
        }
      } catch (e: any) {
        if (!mounted) return;
        setAppErr(e instanceof ApiError ? e.message : String(e));
        setApp(null);
      } finally {
        if (!mounted) return;
        setLoadingApp(false);
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, [slug, storageKey, threadsStorageKey, selectedThreadStorageKey, draftActionStorageKey, actionEnum.length]);

  // Try list threads from backend; fallback to local
  useEffect(() => {
    let mounted = true;
    async function run() {
      if (!slug) return;
      if (!app) return;

      // best-effort backend thread list
      const serverThreads = await tryListThreads(slug, authMode === "api_key" ? apiKey : undefined);
      if (!mounted) return;

      if (serverThreads && serverThreads.length > 0) {
        const normalized: LocalThreadMeta[] = serverThreads
          .map((t) => ({
            id: safeString(t.id),
            title: safeString(t.title || "Chat"),
            created_at: t.created_at,
            updated_at: t.updated_at,
          }))
          .filter((t) => !!t.id);

        normalized.sort((a, b) => (safeString(b.updated_at) || "").localeCompare(safeString(a.updated_at) || ""));
        setThreads(normalized);
        if (threadsStorageKey) setSessionJson(threadsStorageKey, normalized);
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, [slug, app, apiKey, authMode, threadsStorageKey]);

  // load selected thread messages when selectedThreadId changes
  useEffect(() => {
    let mounted = true;
    async function run() {
      if (!selectedThreadId) return;
      setLoadingThread(true);
      setThreadErr(null);
      try {
        const key = authMode === "api_key" ? apiKey : undefined;
        const resp = await getThread(selectedThreadId, key);
        if (!mounted) return;
        setThreadObj(resp.thread || null);
        setMessages(resp.messages || []);

        // update local thread meta updated_at and message_count
        setThreads((prev) => {
          const next = [...prev];
          const idx = next.findIndex((t) => t.id === selectedThreadId);
          const nowIso = new Date().toISOString();
          if (idx >= 0) {
            next[idx] = {
              ...next[idx],
              title: safeString(resp.thread?.title || next[idx].title || "Chat"),
              updated_at: resp.thread?.updated_at || nowIso,
              message_count: resp.messages?.length || 0,
            };
          }
          next.sort((a, b) => (safeString(b.updated_at) || "").localeCompare(safeString(a.updated_at) || ""));
          if (threadsStorageKey) setSessionJson(threadsStorageKey, next);
          return next;
        });

        if (selectedThreadStorageKey) setSessionStr(selectedThreadStorageKey, selectedThreadId);
      } catch (e: any) {
        if (!mounted) return;

        // Prefer structured status if ApiError supports it
        const is404 =
          (e instanceof ApiError && (e as any).status === 404) ||
          String(e?.message || "").includes("404 Not Found") ||
          String(e?.message || "").includes("HTTP 404");

        if (is404) {
          // ✅ if thread not found, clear selection + storage (no auto-create)
          setSelectedThreadId("");
          if (selectedThreadStorageKey) setSessionStr(selectedThreadStorageKey, "");
          setThreadObj(null);
          setMessages([]);
          setThreadErr("Thread not found (it may have been deleted).");
        } else {
          setThreadErr(e instanceof ApiError ? e.message : String(e));
        }
      } finally {
        if (!mounted) return;
        setLoadingThread(false);
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, [selectedThreadId, apiKey, authMode, threadsStorageKey, selectedThreadStorageKey]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  function updateApiKey(v: string) {
    setApiKey(v);
    setApiKeyErr(null);
    if (storageKey) setSessionStr(storageKey, v);
  }

  async function onCreateThread() {
    if (!slug) return;
    if (!app) return;
    if (creatingThread) return; // ✅ hard guard against double-click / re-entrancy
    setCreatingThread(true);

    setThreadErr(null);
    try {
      const key = authMode === "api_key" ? apiKey : undefined;
      if (authMode === "api_key" && !key) {
        setApiKeyErr("This app requires an ingress key. Paste a valid key first.");
        return;
      }
      const t = await createThread(slug, "New chat", key);
      const meta: LocalThreadMeta = {
        id: t.id,
        title: safeString(t.title || "New chat"),
        created_at: t.created_at,
        updated_at: t.updated_at || new Date().toISOString(),
        message_count: 0,
      };


      setThreads((prev) => {
        const next = [meta, ...prev.filter((x) => x.id !== meta.id)];
        if (threadsStorageKey) setSessionJson(threadsStorageKey, next);
        return next;
      });

      // ✅ persist selection immediately (prevents duplicate empty threads)
      setSelectedThreadId(meta.id);
      if (selectedThreadStorageKey) setSessionStr(selectedThreadStorageKey, meta.id);

      setMessageText("");
      setAdvancedInputs({});
    } catch (e: any) {
      setThreadErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setCreatingThread(false);
    }
  }

  function validateInputs(inputs: Record<string, any>) {
    const errs: Record<string, string> = {};
    const required = getRequiredKeys(schema);
    const props = getProperties(schema);

    for (const k of required) {
      const v = inputs[k];
      if (isEmptyValue(v)) {
        errs[k] = "Required";
        continue;
      }

      const p = props[k];
      if (!isObject(p)) continue;

      const t = String((p as any).type || "").toLowerCase();

      // Enum validation (string enums)
      if (Array.isArray((p as any).enum)) {
        const enumList = (p as any).enum.filter((x: any) => typeof x === "string");
        if (typeof v === "string") {
          if (!enumList.includes(v)) errs[k] = `Must be one of: ${enumList.join(", ")}`;
        } else {
          // enum fields should be strings
          errs[k] = `Must be one of: ${enumList.join(", ")}`;
        }
      }
      // String validation
      if (t === "string" && typeof v === "string") {
        const minL = (p as any).minLength;
        const maxL = (p as any).maxLength;
        if (typeof minL === "number" && v.trim().length < minL) errs[k] = `Min length ${minL}`;
        if (typeof maxL === "number" && v.trim().length > maxL) errs[k] = `Max length ${maxL}`;
      }

      // Number/integer validation
      if ((t === "integer" || t === "number") && typeof v === "number") {
        const min = (p as any).minimum;
        const max = (p as any).maximum;
        if (typeof min === "number" && v < min) errs[k] = `Min ${min}`;
        if (typeof max === "number" && v > max) errs[k] = `Max ${max}`;
        if (t === "integer" && !Number.isInteger(v)) errs[k] = "Must be an integer";
      }

      // Boolean validation
      if (t === "boolean" && typeof v !== "boolean") {
        errs[k] = "Must be true or false";
      }
    }

    return errs;
  }

  function buildInputsFromComposer(): { inputs: Record<string, any>; message: string } {
    const inputs: Record<string, any> = {};

    // action field if present in schema enum OR ui fields
    const uiHasAction = uiFields.some((f) => f.key === "action");
    const hasAction = actionEnum.length > 0 || uiHasAction;

    if (hasAction) {
      if (action) inputs["action"] = action;
    }

    // primary text field
    const msg = (messageText || "").trim();

    // If app is "token-only", treat token as primary input
    const req = getRequiredKeys(schema);
    const tokenOnly = req.length === 1 && req[0] === "token";
    if (tokenOnly) {
      inputs["token"] = msg; // message box becomes token
      return { inputs, message: msg };
    }

    if (primaryTextField) {
      // only set if user typed (avoid overwriting advanced)
      if (msg) inputs[primaryTextField] = msg;
    } else if (msg) {
      inputs["text"] = msg;
    }

    // advanced inputs override
    for (const [k, v] of Object.entries(advancedInputs || {})) {
      if (!isEmptyValue(v)) inputs[k] = v;
    }

    return { inputs, message: msg };
  }

  async function refreshThreadNow(threadId: string) {
    const key = authMode === "api_key" ? apiKey : undefined;
    const resp = await getThread(threadId, key);
    setThreadObj(resp.thread || null);
    setMessages(resp.messages || []);
  }

  async function onSend() {
    if (!slug || !app) return;
    setSendErr(null);

    const key = authMode === "api_key" ? apiKey : undefined;
    if (authMode === "api_key" && !key) {
      setApiKeyErr("This app requires an ingress key. Paste a valid key first.");
      return;
    }

    let threadId = selectedThreadId;
    if (!threadId) {
      await onCreateThread();
      // state update is async; re-read from sessionStorage fallback
      threadId = getSessionStr(selectedThreadStorageKey) || selectedThreadId;
      if (!threadId) {
        // fallback: cannot proceed
        setSendErr("Failed to create a thread.");
        return;
      }
    }

    const { inputs, message } = buildInputsFromComposer();
    const hasAnyInput =
      !!(message || "").trim() ||
      Object.entries(advancedInputs || {}).some(([_, v]) => !isEmptyValue(v));

    if (!hasAnyInput) {
      setSendErr("Type a message (or set required fields) before sending.");
      return;
    }
    const coercedInputs = coerceInputsBySchema(inputs, schema);

    // Validate *coerced* inputs (e.g., "7" -> 7 for integer fields)
    const errs = validateInputs(coercedInputs);
    if (Object.keys(errs).length > 0) {
      setSendErr(`Please fix required fields: ${Object.keys(errs).join(", ")}`);
      return;
    }

    // Save draft action per app
    if (draftActionStorageKey && action) setSessionStr(draftActionStorageKey, action);

    // optimistic user message
    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticMsg: IngressMessage = {
      id: optimisticId,
      role: "user",
      content: message || pretty(coercedInputs),
      content_json: { inputs: coercedInputs },
      request_id: null,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimisticMsg]);

    setSending(true);
    try {
      const resp = await submitTurn(
        slug,
        {
          thread_id: threadId,
          message: message || undefined,
          inputs: coercedInputs,
          ignore_memory: !useMemoryDefaults,
        },
        key
      );

      const requestId = resp.request_id;
      setTurns((prev) => ({
        ...prev,
        [requestId]: { request_id: requestId, status: resp.status },
      }));

      const st = await pollUntilTerminal(requestId, key, {
        intervalMs: 500,
        maxAttempts: 80,
        onTick: (s) => {
          setTurns((prev) => {
            const cur = prev[requestId] || { request_id: requestId, status: "queued" };
            return { ...prev, [requestId]: { ...cur, status: s.status, error: s.error || null, finished_at: s.finished_at || null } };
          });
        },
      });

      if (st.status === "failed") {
        setTurns((prev) => {
          const cur = prev[requestId] || { request_id: requestId, status: "failed" };
          return { ...prev, [requestId]: { ...cur, status: "failed", error: st.error || "failed" } };
        });
        setSendErr(st.error || "Request failed.");
        // refresh thread anyway (user message should have been inserted by backend; optimistic will be replaced after refresh)
        await refreshThreadNow(threadId);
        return;
      }

      // Fetch output (this also persists assistant message on backend)
      const out = await getRequestOutput(requestId, key);

      // Best-effort admin request detail for memory audit
      const adminDetail = await tryGetAdminRequestDetail(requestId);

      setTurns((prev) => {
        const cur = prev[requestId] || { request_id: requestId, status: "succeeded" };
        return { ...prev, [requestId]: { ...cur, status: "succeeded", output: out, adminDetail } };
      });

      // Refresh thread messages (authoritative)
      await refreshThreadNow(threadId);

      // clear composer message only (keep advanced values)
      setMessageText("");
    } catch (e: any) {
      setSendErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  async function viewOutput(requestId: string) {
    const key = authMode === "api_key" ? apiKey : undefined;
    try {
      const out = await getRequestOutput(requestId, key);
      const adminDetail = await tryGetAdminRequestDetail(requestId);
      setTurns((prev) => {
        const cur = prev[requestId] || { request_id: requestId, status: "unknown" };
        return { ...prev, [requestId]: { ...cur, output: out, adminDetail } };
      });
    } catch (e: any) {
      setSendErr(e instanceof ApiError ? e.message : String(e));
    }
  }

  const selectedMeta = useMemo(() => threads.find((t) => t.id === selectedThreadId) || null, [threads, selectedThreadId]);
  const threadTitle = safeString(threadObj?.title || selectedMeta?.title || "Chat");

  const optionalAdvancedFields = useMemo(() => {
    // show optional fields: props - required - primaryTextField - action
    const required = new Set(getRequiredKeys(schema));
    const keys = Object.keys(props);
    const out = keys.filter((k) => !required.has(k) && k !== primaryTextField && k !== "action");
    // try to sort: tone/audience first, then rest
    const pref = ["tone", "audience", "format", "output_format"];
    out.sort((a, b) => {
      const ia = pref.indexOf(a);
      const ib = pref.indexOf(b);
      if (ia >= 0 && ib >= 0) return ia - ib;
      if (ia >= 0) return -1;
      if (ib >= 0) return 1;
      return a.localeCompare(b);
    });
    return out;
  }, [props, schema, primaryTextField]);

  if (loadingApp) return <div className="p-6">Loading…</div>;

  if (!tenantUuid) {
    return (
      <div className="p-6">
        <div className="text-xl font-semibold">Missing tenant_id</div>
        <div className="mt-2 text-sm text-gray-600">
          Open this page with <code>?tenant_id=&lt;tenant_uuid&gt;</code>.
        </div>
        <div className="mt-4">
          <Link className="text-blue-700 hover:underline" to="/app/ingress">
            Back to Ingress
          </Link>
        </div>
      </div>
    );
  }

  if (!app || !slug) {
    return (
      <div className="p-6">
        <div className="text-xl font-semibold">Ingress app not found</div>
        {appErr && <div className="mt-2 text-sm text-red-600 whitespace-pre-wrap">{appErr}</div>}
        <div className="mt-4">
          <Link className="text-blue-700 hover:underline" to="/app/ingress">
            Back to Ingress
          </Link>
        </div>
      </div>
    );
  }

  const isPublished = !!app.is_published;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs text-gray-500">
            <Link className="hover:underline" to="/app/ingress">
              Ingress
            </Link>{" "}
            / <span className="font-mono">{slug}</span> / chat
          </div>
          <h2 className="text-xl font-semibold mt-1">{app.name || slug}</h2>
          <div className="text-sm text-gray-600 mt-1">
            Thread: <span className="font-medium">{threadTitle}</span> · auth_mode:{" "}
            <span className="font-mono">{safeString(app.auth_mode || "tenant")}</span>
          </div>
        </div>

        <div className="flex gap-2">
          <Link className="px-3 py-2 rounded border hover:bg-gray-50" to={runnerUrl} title="Back to single request runner">
            Single Request Mode
          </Link>
          <a
            className={`px-3 py-2 rounded border hover:bg-gray-50 ${!isPublished ? "opacity-60 pointer-events-none" : ""}`}
            href={runnerUrl}
            target="_blank"
            rel="noreferrer"
            title={isPublished ? "Open single request runner in new tab" : "Publish app to enable runner"}
          >
            Open Runner
          </a>
        </div>
      </div>

      {appErr && <div className="mt-3 text-sm text-red-600 whitespace-pre-wrap">{appErr}</div>}

      {!isPublished && (
        <div className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          This app is not published. Chat runner is disabled until the app is published.
        </div>
      )}

      {/* Auth mode: api_key */}
      {isPublished && authMode === "api_key" && (
        <div className="mt-4 border rounded bg-white p-4">
          <div className="font-medium text-sm">Ingress Key Required</div>
          <div className="mt-1 text-xs text-gray-500">
            This app requires <code>X-Ingress-Key</code>. Stored in <code>sessionStorage</code> for this slug only.
          </div>

          <div className="mt-3 flex items-center gap-2">
            <input
              className={`w-full border rounded p-2 text-sm font-mono ${apiKeyErr ? "border-red-400" : ""}`}
              placeholder="ing_..."
              value={apiKey}
              onChange={(e) => updateApiKey(e.target.value)}
            />
          </div>
          {apiKeyErr && <div className="mt-2 text-xs text-red-700">{apiKeyErr}</div>}
        </div>
      )}

      {/* Main layout */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: threads */}
        <div className="lg:col-span-4 border rounded bg-white overflow-hidden">
          <div className="p-3 border-b flex items-center justify-between">
            <div className="font-medium text-sm">Threads</div>
            <button
              className="px-2 py-1 rounded bg-gray-900 text-white text-xs hover:bg-black disabled:opacity-60"
              onClick={onCreateThread}
              disabled={!isPublished || sending || loadingThread || creatingThread}
              type="button"
            >
              New chat
            </button>
          </div>

          {threadErr && <div className="p-3 text-xs text-red-700 whitespace-pre-wrap">{threadErr}</div>}

          <div className="max-h-[520px] overflow-auto">
            {threads.length === 0 ? (
              <div className="p-4 text-sm text-gray-600">No threads yet. Create one.</div>
            ) : (
              threads.map((t) => {
                const active = t.id === selectedThreadId;
                return (
                  <button
                    key={t.id}
                    className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 ${active ? "bg-gray-50" : ""}`}
                    onClick={() => setSelectedThreadId(t.id)}
                    type="button"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium truncate">{t.title || "Chat"}</div>
                      {active ? <span className="text-[11px] px-2 py-0.5 rounded border">active</span> : null}
                    </div>
                    <div className="mt-1 text-[11px] text-gray-500">
                      updated: {fmtDt(t.updated_at)} · created: {fmtDt(t.created_at)}
                      {typeof t.message_count === "number" ? ` · msgs: ${t.message_count}` : ""}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right: chat */}
        <div className="lg:col-span-8 border rounded bg-white overflow-hidden flex flex-col">
          <div className="p-3 border-b flex items-center justify-between">
            <div className="text-sm">
              <span className="font-medium">Chat</span>{" "}
              <span className="text-xs text-gray-500">
                (field: <code>{primaryTextField}</code>
                {actionEnum.length > 0 ? (
                  <>
                    {" "}
                    · action enum: <code>{actionEnum.length}</code>
                  </>
                ) : null}
                )
              </span>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-xs text-gray-600 flex items-center gap-2">
                <input type="checkbox" checked={useMemoryDefaults} onChange={(e) => setUseMemoryDefaults(e.target.checked)} />
                Use memory defaults
              </label>

              <button className="text-xs px-2 py-1 rounded border hover:bg-gray-50" type="button" onClick={() => setShowAdvanced((v) => !v)}>
                {showAdvanced ? "Hide Advanced" : "Advanced"}
              </button>
            </div>
          </div>

          {/* transcript */}
          <div className="flex-1 overflow-auto p-4 space-y-3 max-h-[520px]">
            {!selectedThreadId ? (
              <div className="text-sm text-gray-600">
                Select or create a thread to start chatting.
              </div>
            ) : loadingThread ? (
              <div className="text-sm text-gray-600">Loading thread…</div>
            ) : messages.length === 0 ? (
              <div className="text-sm text-gray-600">No messages yet. Send the first message.</div>
            ) : (
              messages.map((m) => {
                const rid = safeString(m.request_id || "");
                const tmeta = rid ? turns[rid] : null;
                return (
                  <div key={m.id} className={`border rounded p-3 ${m.role === "user" ? "bg-white" : "bg-gray-50"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-xs text-gray-500">
                        <span className="px-2 py-0.5 rounded border text-[11px]">{m.role}</span>
                        <span className="ml-2">{fmtDt(m.created_at)}</span>
                        {rid ? (
                          <>
                            <span className="ml-2 text-gray-400">·</span>
                            <span className="ml-2 font-mono">{rid.slice(0, 8)}…</span>
                          </>
                        ) : null}
                      </div>

                      {rid ? (
                        <div className="flex items-center gap-2">
                          <span className={statusBadge(tmeta?.status || "unknown")}>{tmeta?.status || "unknown"}</span>
                          <button className="px-2 py-1 rounded border text-xs hover:bg-white" type="button" onClick={() => viewOutput(rid)}>
                            View Output / Audit
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-2 text-sm">
                      {m.role === "assistant" && m.content_json != null ? (
                        <StructuredValueView value={m.content_json} />
                      ) : (
                        <div className="whitespace-pre-wrap">{m.content}</div>
                      )}
                    </div>

                    {/* NEW: Feedback per assistant turn */}
                    {m.role === "assistant" && rid ? (
                      <FeedbackWidget
                        ingressRequestId={rid}
                        appSlug={slug}
                        ingressKey={authMode === "api_key" ? apiKey : undefined}
                        compact
                      />
                    ) : null}

                    {m.content_json != null ? (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-gray-600">Pretty JSON</summary>
                        <pre className="mt-2 text-xs bg-white border rounded p-2 overflow-auto max-h-[260px]">
                          {pretty(m.content_json)}
                        </pre>
                      </details>
                    ) : null}

                    {rid && tmeta?.output ? (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-xs text-gray-600">Request details</summary>

                        <div className="mt-2 text-xs text-gray-700">
                          <div>
                            request_id: <code>{rid}</code>
                          </div>
                          {tmeta.error ? <div className="mt-1 text-red-700">error: {tmeta.error}</div> : null}
                        </div>

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="border rounded p-2">
                            <div className="text-xs font-medium text-gray-700">Final output</div>
                            <pre className="mt-2 text-xs bg-white border rounded p-2 overflow-auto max-h-[220px]">
                              {pretty({
                                final_output_kind: tmeta.output?.final_output_kind,
                                final_output_path: tmeta.output?.final_output_path,
                                final_output: tmeta.output?.final_output,
                              })}
                            </pre>
                          </div>

                          <div className="border rounded p-2">
                            <div className="text-xs font-medium text-gray-700">Policy summary</div>
                            <pre className="mt-2 text-xs bg-white border rounded p-2 overflow-auto max-h-[220px]">
                              {pretty(tmeta.output?.policy_summary ?? null)}
                            </pre>
                          </div>
                        </div>

                        <div className="mt-3 border rounded p-2">
                          <div className="text-xs font-medium text-gray-700">Memory audit</div>
                          {tmeta.adminDetail ? (
                            (() => {
                              const { rows } = buildAuditRows(tmeta.adminDetail, tmeta.output);
                              if (!rows.length) {
                                return <div className="mt-2 text-xs text-gray-600">No memory audit fields found.</div>;
                              }
                              return (
                                <div className="mt-2 overflow-auto">
                                  <table className="min-w-full text-xs">
                                    <thead className="text-gray-600">
                                      <tr className="border-b">
                                        <th className="text-left py-1 pr-2">key</th>
                                        <th className="text-left py-1 pr-2">snapshot</th>
                                        <th className="text-left py-1 pr-2">applied?</th>
                                        <th className="text-left py-1 pr-2">effective</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {rows.map((r) => (
                                        <tr key={r.key} className="border-b">
                                          <td className="py-1 pr-2 font-mono">{r.key}</td>
                                          <td className="py-1 pr-2 whitespace-pre-wrap">{pretty(r.snapshotVal)}</td>
                                          <td className="py-1 pr-2">{r.applied ? "yes" : "no"}</td>
                                          <td className="py-1 pr-2 whitespace-pre-wrap">{pretty(r.effectiveVal)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              );
                            })()
                          ) : (
                            <div className="mt-2 text-xs text-gray-600">
                              Audit detail not available via public endpoints. (If admin request detail endpoint is enabled, it will appear here automatically.)
                            </div>
                          )}
                        </div>

                        <details className="mt-3">
                          <summary className="cursor-pointer text-xs text-gray-600">Raw output JSON</summary>
                          <pre className="mt-2 text-xs bg-white border rounded p-2 overflow-auto max-h-[320px]">
                            {pretty(tmeta.output)}
                          </pre>
                        </details>
                      </details>
                    ) : null}
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* composer */}
          <div className="border-t p-3">
            {sendErr && <div className="mb-2 text-xs text-red-700 whitespace-pre-wrap">{sendErr}</div>}

            {actionEnum.length > 0 ? (
              <div className="mb-2">
                <div className="text-[11px] text-gray-600">Action</div>
                <select
                  className="mt-1 w-full border rounded p-2 text-sm"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  disabled={!isPublished || sending}
                >
                  {actionEnum.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {/* Required fields (always shown): enums + numbers */}
            {(requiredEnumFields.length > 0 || requiredNumberFields.length > 0) ? (
              <div className="mb-3 border rounded p-3 bg-gray-50">
                <div className="text-xs font-medium text-gray-700">Required</div>

                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {/* enums */}
                  {requiredEnumFields.map((f) => (
                    <div key={f.key}>
                      <div className="text-[11px] text-gray-600">
                        {f.key} <span className="text-red-600">*</span>
                      </div>
                      <select
                        className="mt-1 w-full border rounded p-2 text-sm"
                        value={safeString(advancedInputs[f.key])}
                        onChange={(e) => setAdvancedInputs((p) => ({ ...p, [f.key]: e.target.value }))}
                        disabled={!isPublished || sending}
                      >
                        {f.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}

                  {/* numbers */}
                  {requiredNumberFields.map((f) => (
                    <div key={f.key}>
                      <div className="text-[11px] text-gray-600">
                        {f.key} <span className="text-red-600">*</span>
                      </div>
                      <input
                        className="mt-1 w-full border rounded p-2 text-sm"
                        type="number"
                        step={f.type === "integer" ? 1 : "any"}
                        min={f.minimum}
                        max={f.maximum}
                        value={safeString(advancedInputs[f.key])}
                        onChange={(e) => setAdvancedInputs((p) => ({ ...p, [f.key]: e.target.value }))}
                        disabled={!isPublished || sending}
                        placeholder={f.type === "integer" ? "0-99" : "number"}
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-2 text-[11px] text-gray-500">
                  These are required by the app schema and must be sent on every turn.
                </div>
              </div>
            ) : null}


            {showAdvanced && optionalAdvancedFields.length > 0 ? (
              <div className="mb-3 border rounded p-3 bg-gray-50">
                <div className="text-xs font-medium text-gray-700">Advanced (optional inputs override memory)</div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {optionalAdvancedFields.map((k) => (
                    <div key={k}>
                      <div className="text-[11px] text-gray-600">{k}</div>
                      <input
                        className="mt-1 w-full border rounded p-2 text-sm"
                        value={safeString(advancedInputs[k])}
                        onChange={(e) => setAdvancedInputs((p) => ({ ...p, [k]: e.target.value }))}
                        placeholder="(optional)"
                        disabled={!isPublished || sending}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex items-end gap-2">
              <div className="flex-1">
                <div className="text-[11px] text-gray-600">
                  Message{" "}
                  <span className="text-gray-400">
                    (fills <code>{primaryTextField}</code>
                    {requiredKeys.includes("token") && requiredKeys.length === 1 ? <> / token</> : null})
                  </span>
                </div>
                <textarea
                  className="mt-1 w-full border rounded p-2 text-sm h-20"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Type your message…"
                  disabled={!isPublished || sending}
                />
              </div>

              <button
                className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-black disabled:opacity-60"
                type="button"
                onClick={onSend}
                disabled={!isPublished || sending}
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>

            <div className="mt-2 text-[11px] text-gray-500">
              Turn lifecycle: submit → poll status → fetch output (persists assistant message) → refresh thread.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}